/**
 * Axios Interceptor cho Seq Logging
 * Tự động log tất cả API requests và responses
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { loggerService } from './logger';

/**
 * Setup axios interceptors để tự động log API calls
 */
export const setupAxiosLogger = () => {
  // Request interceptor - Log trước khi gửi request
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const startTime = Date.now();
      
      // Lưu start time vào config để tính duration sau
      (config as any).metadata = { startTime };
      
      // Log request
      loggerService.logApiRequest(
        config.method?.toUpperCase() || 'GET',
        config.url || '',
        {
          headers: config.headers,
          params: config.params,
          data: config.data,
        }
      );
      
      return config;
    },
    (error: AxiosError) => {
      // Log request error
      loggerService.error('API Request Failed', error as Error, {
        eventType: 'ApiRequestError',
      });
      return Promise.reject(error);
    }
  );

        // Response interceptor - Log sau khi nhận response
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      const config = response.config as any;
      
      // Kiểm tra config tồn tại trước khi xử lý
      if (!config) {
        return response;
      }
      
      const metadata = config.metadata;
      const duration = metadata?.startTime 
        ? Date.now() - metadata.startTime 
        : 0;
      
      // Log response thành công
      loggerService.logApiResponse(
        config.method?.toUpperCase() || 'GET',
        config.url || '',
        response.status,
        {
          duration,
          data: response.data,
          headers: response.headers,
        }
      );
      
      // Log performance nếu request chậm (> 2s)
      if (duration > 2000) {
        loggerService.warn('Slow API Request', {
          method: config.method?.toUpperCase(),
          url: config.url,
          duration,
          eventType: 'SlowApiRequest',
        });
      }
      
      return response;
    },
        (error: AxiosError) => {
      // Safely get config and metadata
      const config = error.config as any;
      const metadata = config?.metadata;
      const duration = metadata?.startTime 
        ? Date.now() - metadata.startTime 
        : 0;
      
      // Log response error only if we have config
      if (config) {
        loggerService.logApiError(
          config.method?.toUpperCase() || 'GET',
          config.url || 'unknown',
          error as Error
        );
      } else {
        // Log error without config (e.g., request cancelled or blocked)
        loggerService.error('API Request Failed', error as Error, {
          eventType: 'ApiRequestBlocked',
        });
      }
      
      // Log thêm thông tin chi tiết về error
      if (error.response) {
        // Server đã phản hồi với status code ngoài 2xx
        loggerService.error('API Error Response', error as Error, {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          duration,
          eventType: 'ApiErrorResponse',
        });
      } else if (error.request) {
        // Request đã được gửi nhưng không nhận được response
        loggerService.error('API No Response', error as Error, {
          duration,
          eventType: 'ApiNoResponse',
        });
      } else {
        // Lỗi khi setup request
        loggerService.error('API Setup Error', error as Error, {
          eventType: 'ApiSetupError',
        });
      }
      
      return Promise.reject(error);
    }
  );
};

/**
 * Remove axios logging interceptors (để testing hoặc debugging)
 */
export const removeAxiosLogger = () => {
  axios.interceptors.request.clear();
  axios.interceptors.response.clear();
};